'use client';
// La frise guidée d'une NC : chaque étape est un état ET un onglet (RQ, 03/10/2026).
// La logique (où en est-on, quelle étape suit) est dans `lib/parcours-nc.ts`.
import React from 'react';
import { I } from '@/components/ui';
import { fmtDate } from '@/lib/meta';
import {
  REPERES, NB_REPERES, numerosAffiches, etape, etatEtape, etapeCourante, suivante, precedente, type EtapeNC,
} from '@/lib/parcours-nc';

import { Bouton } from '@/shared/Bouton';
export type OngletParcours = EtapeNC | 'liens';

export function FriseEtapes({ statut, actif, onChange, dates, repereActif }: {
  statut: string; actif: OngletParcours;
  /** Le repère affiché, quand plusieurs partagent une étape (les sections 1 à 3
   *  du constat sont des onglets) — sinon tous ceux de l'étape active. */
  repereActif?: number;
  /** L'étape à ouvrir et, pour les repères du constat, la section où descendre. */
  onChange: (k: EtapeNC, ancre?: string) => void;
  dates: Partial<Record<EtapeNC, string | undefined | null>>;
}) {
  /* SIX REPÈRES (13/10/2026) : les trois sections de la déclaration, puis
     l'analyse, le plan d'actions et la clôture — voir `REPERES`. L'état de
     chaque repère est celui de l'étape du parcours qui le porte. */
  const surTouche = (e: React.KeyboardEvent, i: number) => {
    const d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (!d) return;
    e.preventDefault();
    const r = REPERES[(i + d + REPERES.length) % REPERES.length];
    onChange(r.onglet, r.ancre);
  };
  return (
    <div role="tablist" aria-label="Étapes de la non-conformité"
      style={{ display: 'flex', alignItems: 'stretch', gap: 0, borderBottom: '1px solid var(--border-default)', flexWrap: 'wrap' }}>
      {REPERES.map((r, i) => {
        const e = etape(r.onglet);
        const etat = etatEtape(r.onglet, statut);
        const on = repereActif !== undefined ? repereActif === r.numero : actif === r.onglet;
        const faite = etat === 'faite';
        const encours = etat === 'en_cours';
        // La date ne se dit qu'une fois par étape : sur son PREMIER repère.
        const premier = REPERES.findIndex((x) => x.onglet === r.onglet) === i;
        return (
          <button key={r.numero} type="button" role="tab" id={`nc-tab-${r.numero}`} aria-selected={on} aria-controls={`nc-panel-${r.onglet}`}
            aria-current={encours && premier ? 'step' : undefined} tabIndex={on && premier ? 0 : -1} onKeyDown={(ev) => surTouche(ev, i)}
            onClick={() => onChange(r.onglet, r.ancre)}
            title={`${e.question} — ${faite ? 'étape franchie' : encours ? 'étape en cours' : 'étape à venir'}`}
            className="ged-btn"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, border: 'none', cursor: 'pointer', background: 'transparent',
              fontFamily: 'var(--font-sans)', padding: '9px 12px 9px 8px', marginBottom: -1,
              borderBottom: `2px solid ${on ? 'var(--color-primary)' : 'transparent'}`,
              color: on ? 'var(--color-primary-active)' : 'var(--text-muted)',
            }}>
            <span aria-hidden style={{
              display: 'grid', placeItems: 'center', width: 24, height: 24, borderRadius: '50%', flex: 'none',
              fontSize: 12, fontWeight: 800,
              background: faite ? 'var(--color-primary)' : encours ? 'var(--cyan-50)' : 'var(--surface-card)',
              color: faite ? 'var(--surface-card)' : encours ? 'var(--color-primary-active)' : 'var(--text-muted)',
              border: `2px solid ${faite || encours ? 'var(--color-primary)' : 'var(--border-strong)'}`,
            }}>{faite ? <I n="check" s={12} /> : r.numero}</span>
            <span style={{ display: 'grid', textAlign: 'start', lineHeight: 1.25 }}>
              <span style={{ fontSize: 13, fontWeight: on || encours ? 700 : 600 }}>{r.titre}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-faint, var(--text-muted))' }}>
                {!premier ? '' : faite || encours ? (dates[r.onglet] ? fmtDate(dates[r.onglet] as string) : (encours ? 'en cours' : '')) : 'à venir'}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** « Étape 4 sur 6 — Analyse : Pourquoi est-ce arrivé ? » + ce qu'on attend + ce qui manque. */
export function ConsigneEtape({ cle, statut, reste }: { cle: EtapeNC; statut: string; reste: string[] }) {
  /* DISCRÈTE (RQ, 13/10/2026 : « l'encadré qui explique, à chaque onglet, est
     beaucoup trop proéminent »). Une ligne, sans cadre ni fond : où l'on en est,
     la consigne en clair, et seulement ce qui manque en couleur. La question de
     l'étape reste dans l'infobulle des repères de la frise. */
  const e = etape(cle);
  const etat = etatEtape(cle, statut);
  const courante = etapeCourante(statut) === cle;
  return (
    <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-muted)' }}>
      <strong style={{ color: 'var(--text-body)' }}>
        {numerosAffiches(cle).includes(' à ') ? 'Étapes' : 'Étape'} {numerosAffiches(cle)} sur {NB_REPERES}
      </strong>
      {etat === 'faite' && ' · franchie'}
      {etat === 'a_venir' && ' · à venir'}
      {courante && ' · en cours'}
      {' — '}{e.consigne}
      {reste.length > 0 && etat !== 'a_venir' && (
        <span style={{ color: 'var(--amber-700, #8A5B00)', fontWeight: 600 }}> À compléter : {reste.join(' · ')}.</span>
      )}
    </p>
  );
}

/** Pied d'étape : retour, et l'étape suivante — avec, si elle en est une, la transition d'état. */
export function NavEtapes({ cle, onAller, transition }: {
  cle: EtapeNC; onAller: (k: EtapeNC) => void;
  /** Si l'étape suivante demande un changement d'état, son bouton (libellé, action, occupé). */
  transition?: { libelle: string; occupe: boolean; faire: () => void } | null;
}) {
  const av = precedente(cle);
  const ap = suivante(cle);
  if (!av && !ap) return null;
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
      {av && (
        <Bouton taille="sm" type="button" onClick={() => onAller(av)}>
          <I n="chevron-left" s={12} />Précédent : {etape(av).titre}
        </Bouton>
      )}
      <span style={{ flex: 1 }} />
      {ap && (transition
        ? (
          <Bouton variante="primaire" type="button" disabled={transition.occupe} onClick={transition.faire}>
            {transition.occupe ? 'En cours…' : transition.libelle}<I n="chevron-right" s={13} />
          </Bouton>
        ) : (
          <Bouton variante="primaire" type="button" onClick={() => onAller(ap)}>
            Suivant : {etape(ap).titre}<I n="chevron-right" s={13} />
          </Bouton>
        ))}
    </div>
  );
}
